import React from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  BackgroundVariant,
  Node,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import type { ReactFlowProps } from '@xyflow/react';

interface FlowCanvasProps extends ReactFlowProps {
  /** Returns a fill colour for each node in the MiniMap. */
  miniMapNodeColor?: (node: Node) => string;
  /** Rendered centred over the canvas when `nodes` is empty. */
  emptyState?: React.ReactNode;
}

/**
 * FlowCanvas — a ReactFlow wrapper pre-configured with Background (dots),
 * Controls, and MiniMap. Pass all standard ReactFlow props through; use
 * `miniMapNodeColor` for per-type colouring and `emptyState` for the
 * empty-canvas hint overlay.
 */
const FlowCanvas: React.FC<FlowCanvasProps> = ({
  miniMapNodeColor,
  emptyState,
  nodes = [],
  children,
  ...rest
}) => {
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <ReactFlow nodes={nodes} fitView {...rest}>
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#e2e8f0" />
        <Controls />
        <MiniMap
          nodeColor={miniMapNodeColor}
          style={{ border: '1px solid var(--mg-border)' }}
        />
        {children}
      </ReactFlow>

      {nodes.length === 0 && emptyState && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          {emptyState}
        </div>
      )}
    </div>
  );
};

export default FlowCanvas;
